import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Input, Select, Button, Avatar, Tag, Modal, Form,
  Radio, Card, Spin, DatePicker, Switch, message, Space, Typography,
} from 'antd';
import { SearchOutlined, CrownOutlined, UserOutlined, CheckCircleOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getAdminUsersApi, addSubscriptionApi, getAdminPlansApi } from '../../api/admin';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate, formatCurrency } from '../../utils/formatters';
import SendNotificationModal from '../../components/SendNotificationModal';

const { Title, Text } = Typography;

const SUBSCRIBED_OPTIONS = [
  { value: '', label: 'All Users' },
  { value: '1', label: 'Prime' },
  { value: '0', label: 'Free' },
];

const WEB_PLANS = ['kh_3', 'kh_4'];

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [meta, setMeta]             = useState({ total: 0, current_page: 1, per_page: 20 });
  const [search, setSearch]         = useState('');
  const [subscribed, setSubscribed] = useState('');
  const [modalOpen, setModalOpen]   = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans]           = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [form] = Form.useForm();
  const [notifUser, setNotifUser] = useState(null);

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, per_page: meta.per_page, sort_by: 'create_at', sort_dir: 'desc' };
      if (search)         params.search     = search;
      if (subscribed !== '') params.subscribed = subscribed;
      const res = await getAdminUsersApi(params);
      setUsers(res.data.data || []);
      setMeta(res.data.meta || meta);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, subscribed, meta.per_page]);

  useEffect(() => { fetchUsers(1); }, [search, subscribed]);

  const openSubscription = async (user) => {
    setSelectedUser(user);
    setSelectedPlanId(null);
    form.resetFields();
    setModalOpen(true);
    setLoadingPlans(true);
    try {
      const res = await getAdminPlansApi();
      setPlans(res.data?.data || res.data || []);
    } catch {
      message.error('Failed to load plans');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSubscribe = async (values) => {
    setSubmitting(true);
    try {
      await addSubscriptionApi({ user_id: selectedUser.id, plan_id: values.plan_id, expire_on: values.expire_on.format('YYYY-MM-DD'), web_access: values.web_access ? 1 : 0 });
      const planName = plans.find((p) => p.subscription_id === values.plan_id)?.name || 'Plan';
      message.success(`${planName} plan activated for ${selectedUser.name}`);
      setModalOpen(false);
      fetchUsers(meta.current_page);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to update subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPlan = plans.find((p) => p.subscription_id === selectedPlanId);

  const columns = [
    {
      title: 'User',
      key: 'user',
      width: 220,
      render: (_, r) => (
        <Space>
          <Avatar
            src={r.photo ? USER_IMAGE(r.photo) : null}
            icon={<UserOutlined />}
            size={36}
            style={{ background: '#e8f5e9', color: '#1a6b2a', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.country_code} {r.mobile}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 140,
      render: (_, r) => (
        <Text style={{ fontSize: 13 }}>{[r.city, r.state].filter(Boolean).join(', ') || '-'}</Text>
      ),
    },
    {
      title: 'Status',
      key: 'prime',
      width: 100,
      render: (_, r) => r.isPrime
        ? <Tag color="gold" icon={<CrownOutlined />}>Prime</Tag>
        : <Tag color="default">Free</Tag>,
    },
    {
      title: 'Plan ID',
      dataIndex: 'plan_subscription_id',
      width: 120,
      render: (v) => v
        ? <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Tag>
        : <Text style={{ color: '#d9d9d9' }}>—</Text>,
    },
    {
      title: 'Expires',
      dataIndex: 'expireOn',
      width: 120,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
    {
      title: 'Last Open',
      dataIndex: 'last_open_at',
      width: 110,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
    {
      title: 'App Ver',
      dataIndex: 'app_version',
      width: 80,
      render: (v) => <Text style={{ fontSize: 12, color: '#9ca3af' }}>{v || '-'}</Text>,
    },
    {
      title: 'Joined',
      dataIndex: 'create_at',
      width: 110,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, r) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            icon={<CrownOutlined />}
            style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}
            onClick={() => openSubscription(r)}
          >
            {r.isPrime ? 'Renew' : 'Add Prime'}
          </Button>
          <Button
            size="small"
            icon={<SendOutlined />}
            onClick={() => setNotifUser(r)}
          >
            Send
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>User Management</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Total: {meta.total} users</Text>
        </div>
        <Space wrap>
          <Select
            value={subscribed}
            onChange={setSubscribed}
            options={SUBSCRIBED_OPTIONS}
            style={{ width: 130 }}
          />
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search name, mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 220 }}
          />
        </Space>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={users}
        loading={loading}
        scroll={{ x: 900 }}
        onRow={(r) => ({
          onClick: () => navigate(`/admin/users/${r.id}`, { state: { user: r } }),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
          showSizeChanger: false,
          showTotal: (total) => `${total} users`,
          onChange: (page) => fetchUsers(page),
        }}
        size="small"
      />

      <Modal
        open={modalOpen}
        title={
          <Space>
            <CrownOutlined style={{ color: '#d4a017' }} />
            {selectedUser?.isPrime ? 'Renew Subscription' : 'Add Subscription'} — {selectedUser?.name}
          </Space>
        }
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubscribe} style={{ marginTop: 16 }}>
          <Form.Item name="plan_id" rules={[{ required: true, message: 'Select a plan' }]}>
            {loadingPlans ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            ) : (
              <Radio.Group
                style={{ width: '100%' }}
                onChange={(e) => {
                  const plan = plans.find((p) => p.subscription_id === e.target.value);
                  setSelectedPlanId(e.target.value);
                  if (plan) form.setFieldValue('expire_on', dayjs().add(plan.duration_days, 'day'));
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {plans.map((plan) => {
                    const hasWeb   = WEB_PLANS.includes(plan.subscription_id);
                    const duration = plan.duration_days >= 365
                      ? `${Math.round(plan.duration_days / 365)} year`
                      : `${plan.duration_days} days`;
                    return (
                      <Radio
                        key={plan.subscription_id}
                        value={plan.subscription_id}
                        style={{ width: '100%' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 6 }}>
                          <div>
                            <Text strong>{plan.name}</Text>
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                              {duration} · {hasWeb ? 'App + Web ✓' : 'App only'}
                            </Text>
                          </div>
                          <Text strong style={{ color: '#1a6b2a' }}>{formatCurrency(plan.price)}</Text>
                        </div>
                      </Radio>
                    );
                  })}
                </Space>
              </Radio.Group>
            )}
          </Form.Item>

          {selectedPlan && (
            <Card
              size="small"
              style={{ background: '#f6ffed', borderColor: '#b7eb8f', marginBottom: 4, borderRadius: 8 }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 13 }}>
                <div><Text type="secondary">Plan:</Text> <Text strong>{selectedPlan.name}</Text></div>
                <div><Text type="secondary">Price:</Text> <Text strong>{formatCurrency(selectedPlan.price)}</Text></div>
                <div><Text type="secondary">Duration:</Text> <Text>{selectedPlan.duration_days} days</Text></div>
                <div><Text type="secondary">Web Access:</Text> <Text>{WEB_PLANS.includes(selectedPlan.subscription_id) ? 'Yes' : 'No'}</Text></div>
              </div>
            </Card>
          )}

          <Form.Item
            label="Expire On"
            name="expire_on"
            rules={[{ required: true, message: 'Select expiry date' }]}
            style={{ marginTop: 12 }}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD MMM YYYY"
              disabledDate={(d) => d && d.isBefore(dayjs(), 'day')}
            />
          </Form.Item>

          <Form.Item label="Web Access" name="web_access" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={!selectedPlanId}
              icon={<CheckCircleOutlined />}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
            >
              {selectedUser?.isPrime ? 'Renew' : 'Activate'}
            </Button>
          </div>
        </Form>
      </Modal>

      <SendNotificationModal
        open={!!notifUser}
        onClose={() => setNotifUser(null)}
        user={notifUser}
      />
    </div>
  );
}
